import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Barcode } from "@phosphor-icons/react";

/**
 * Dedicated barcode scan input dialog. Renders an autofocused input
 * so a scanner (or the server's keyboard) can send the code and hit
 * Enter to submit. Used from the POS toolbar as an explicit "Scan"
 * escape hatch alongside the always-on document-level scanner.
 */
const BarcodeScanDialog = ({ open, onOpenChange, onScan }) => {
  const [value, setValue] = useState("");

  useEffect(() => {
    if (open) setValue("");
  }, [open]);

  const submit = () => {
    const code = value.trim();
    if (!code) return;
    onScan(code);
    setValue("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="barcode-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Barcode size={22} /> Scan a Barcode
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-sm text-muted-foreground">
            Point the scanner at a menu item&apos;s barcode, or type the code and press Enter.
          </p>
          <Input
            autoFocus
            placeholder="Scan or type code..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            className="font-mono text-lg"
            data-testid="barcode-scan-input"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="barcode-scan-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!value.trim()} data-testid="barcode-scan-submit">
            Add to Ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanDialog;
